import { ApplicationRef, createComponent, EnvironmentInjector, inject, Injectable } from '@angular/core';
import { limit, orderBy, where } from '@angular/fire/firestore';
import { TranslateService } from '@ngx-translate/core';
import { LoadingService } from '@scandium-oy/ngx-scandium';
import { format } from 'date-fns';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Note } from '../models/note.model';
import { IService } from '../models/service.interface';
import { noteA4Css } from '../notes/component/note-style';
import { NotePrintComponent } from '../notes/print/note-print.component';
import { convertTimestamp, setDates } from '../utility/time';
import { ContractorsService } from './contractors.service';
import { FirestoreService } from './firestore.service';
import { PrintService } from './print.service';

const itemCollection = 'notes';

interface PrintableNote {
  date?: Date | Note['date'] | null;
  room?: string | null;
  site?: string | null;
  sitename?: string | null;
  text?: string | null;
  username?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class NotesService implements IService<Note> {
  private appRef = inject(ApplicationRef);
  private contractorsService = inject(ContractorsService);
  private environmentInjector = inject(EnvironmentInjector);
  private firestore = inject(FirestoreService);
  private loadingService = inject(LoadingService);
  private printService = inject(PrintService);
  private translate = inject(TranslateService);


  async save(note: Note) {
    return this.firestore.save(itemCollection, note);
  }

  async update(item: Note) {
    return this.firestore.update(itemCollection, item);
  }

  async delete(item: Note) {
    return this.firestore.delete(itemCollection, item);
  }

  get(guid: string): Observable<Note> {
    return this.firestore.get<Note>(itemCollection, guid);
  }

  getList(options?: { site?: string; contractor?: string; creator?: string }): Observable<Note[]> {
    const queryConstraints = [];
    if (options?.site) {
      queryConstraints.push(where('site', '==', options.site));
    }
    if (options?.contractor) {
      queryConstraints.push(where('contractor', '==', options.contractor));
    }
    if (options?.creator) {
      queryConstraints.push(where('user', '==', options.creator));
    }

    return this.firestore
      .getList<Note>(itemCollection, undefined, queryConstraints)
      .pipe(map((results) => results.map((it) => setDates(it, 'date')) ?? []));
  }

  getLatestForContractor(contractor: string, maximum = 5): Observable<Note[]> {
    return this.firestore
      .getList<Note>(itemCollection, undefined, [
        where('contractor', '==', contractor),
        orderBy('date', 'desc'),
        limit(maximum),
      ])
      .pipe(map((results) => results.map((it) => setDates(it, 'date')) ?? []));
  }

  async print(note: PrintableNote) {
    this.loadingService.showLoading(undefined, 'general.printing', ['full-loading']);

    const noteDate = note.date ? convertTimestamp(note.date) : new Date();
    const siteName = note.sitename ?? note.site ?? '-';
    const name = `muistio_${siteName}_${noteDate.toISOString()}`;
    const siteLabel = this.translate.instant('timesheet.site') as string;
    const userLabel = this.translate.instant('users.title') as string;
    const dateLabel = this.translate.instant('general.date') as string;
    const roomLabel = this.translate.instant('calendar.site.area') as string;
    const title = this.translate.instant('report.new.list.note') as string;

    const host = document.createElement('div');
    document.body.appendChild(host);

    const componentRef = createComponent(NotePrintComponent, {
      environmentInjector: this.environmentInjector,
      hostElement: host,
    });
    this.appRef.attachView(componentRef.hostView);
    componentRef.setInput('vm', {
      siteLabel,
      userLabel,
      dateLabel,
      roomLabel,
      siteName,
      username: note.username ?? '-',
      formattedDate: format(noteDate, 'd.M.yyyy HH:mm'),
      room: note.room ?? null,
      text: note.text ?? '',
    });
    componentRef.changeDetectorRef.detectChanges();

    const element = host.firstElementChild as HTMLElement ?? host;

    return this.printService.printA4Document(name, element, {
      css: noteA4Css,
      scale: 2,
      header: {
        title,
        dateLabel: format(noteDate, 'd.M.yyyy'),
        brandName: this.contractorsService.contractorS()?.name,
        logoUrl: this.contractorsService.contractorS()?.logo,
      },
    }).then((blob) => {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      host.remove();
      this.loadingService.hideLoading();
      return blob;
    }).catch((error) => {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      host.remove();
      console.error(error);
      this.loadingService.hideLoading();
      return null;
    });
  }
}
